<template>
  <FormContainer @submitForm="saveCosto">
    <div class="row">
      <FormInputTextWidget
        class="col-lg-4 col-sm-6"
        label="Categoria"
        v-model="costo.name"
        :errorMsg="errorHash['name']"
        @input="removeError('name')"
      />

      <FormInputIntegerWidget
        class="col-lg-4 col-sm-6"
        label="Numero persone"
        v-model.number="costo.nbPeople"
        :errorMsg="errorHash['nbPeople']"
        @input="removeError('nbPeople')"
      />

      <FormInputIntegerWidget
        class="col-lg-4 col-sm-6"
        label="Ore mensili (individuali)"
        v-model.number="costo.monthHours"
        :errorMsg="errorHash['monthHours']"
        @input="removeError('monthHours')"
        not-required
      />
    </div>

    <div class="row">
      <FormInputAmountWidget
        class="col-lg-4 col-sm-6"
        label="RAL"
        v-model.number="costo.ral"
        :min="0"
        :errorMsg="errorHash['ral']"
        @input="removeError('ral')"
      />

      <FormInputSelectWidget
        class="col-lg-4 col-sm-6"
        label="Mensilità"
        v-model="costo.mensilita"
        :options="mensilitaOptions"
      />

      <FormInputPercentageWidget
        class="col-lg-4 col-sm-6"
        label="Ritenuta fiscale"
        v-model.number="costo.ritenuta"
        :errorMsg="errorHash['ritenuta']"
        @input="removeError('ritenuta')"
      />

      <FormInputPercentageWidget
        class="col-lg-4 col-sm-6"
        label="Variazione annuale"
        v-model.number="costo.delta"
        :errorMsg="errorHash['delta']"
        @input="removeError('delta')"
      />

      <FormInputPercentageWidget
        class="col-lg-4 col-sm-6"
        label="Oneri sociali"
        v-model.number="costo.oneriSociali"
        :errorMsg="errorHash['oneriSociali']"
        @input="removeError('oneriSociali')"
      />

      <FormInputPercentageWidget
        class="col-lg-4 col-sm-6"
        label="Accantonamento TFR"
        v-model.number="costo.accantonamentoTfr"
        :errorMsg="errorHash['accantonamentoTfr']"
        @input="removeError('accantonamentoTfr')"
      />

      <FormInputAmountWidget
        class="col-lg-4 col-sm-6"
        label="Altri costi"
        v-model.number="costo.altriCosti"
        :min="0"
        :errorMsg="errorHash['altriCosti']"
        @input="removeError('altriCosti')"
      />

      <FormInputMonthPickerWidget
        class="col-lg-4 col-sm-6"
        label="Mese di assunzione"
        v-model="costo.begin"
        :errorMsg="errorHash['begin']"
        @input="removeError('begin')"
      />

      <FormInputMonthPickerWidget
        class="col-lg-4 col-sm-6"
        label="Mese di cessazione"
        not-required
        v-model="costo.end"
        :errorMsg="errorHash['end']"
        @input="removeError('end')"
      />
    </div>

    <div class="row">
      <div class="col-12 mt-4 text-center text-sm-right">
        <button class="btn btn-primary" type="submit">
          <i class="fas fa-save mr-2"></i>
          Salva costo
        </button>
      </div>
    </div>
  </FormContainer>
</template>

<script>

import validateMixin from '@/libs/Form/mixins/validate';

export default {
  name: 'operative-form-cost',
  mixins: [validateMixin],
  components: {
    FormInputPercentageWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputPercentageWidget'),
    FormContainer: () => import('@/libs/Form/components/FormContainer'),
    FormInputTextWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputTextWidget'),
    FormInputIntegerWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputIntegerWidget'),
    FormInputAmountWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputAmountWidget'),
    FormInputSelectWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputSelectWidget'),
    FormInputMonthPickerWidget: () => import('@/libs/Form/components/Inputs/Widgets/FormInputMonthPickerWidget'),
  },
  props: {
    costo: {
      type: Object,
      default: () => ({
        name: null,
        nbPeople: 1,
        monthHours: 160,
        ral: null,
        mensilita: 12,
        ritenuta: 33,
        delta: 0,
        oneriSociali: 30,
        accantonamentoTfr: 8,
        altriCosti: 0,
        begin: null,
        end: null,
      }),
    },
    mensilitaOptions: {
      type: Array,
      default: () => [
        { value: 12, label: '12 mensilità' },
        { value: 13, label: '13 mensilità' },
        { value: 14, label: '14 mensilità' },
      ],
    },
    edit: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    validate () {
      this.clearErrors();

      if (!this.costo.name) {
        this.addError('La categoria è obbligatoria', 'name');
      }

      if (!this.costo.nbPeople) {
        this.addError('Il numero di persone è obbligatorio', 'nbPeople');
      }

      if (!this.costo.ral) {
        this.addError('Il RAL è obbligatorio', 'ral');
      }

      if (!this.costo.ritenuta && this.costo.ritenuta !== 0) {
        this.addError('La ritenuta fiscale è obbligatoria', 'ritenuta');
      }

      if (!this.costo.delta && this.costo.delta !== 0) {
        this.addError('La variazione annuale è obbligatoria', 'delta');
      }

      if (!this.costo.oneriSociali && this.costo.oneriSociali !== 0) {
        this.addError('Gli oneri sociali sono obbligatori', 'oneriSociali');
      }

      if (!this.costo.accantonamentoTfr && this.costo.accantonamentoTfr !== 0) {
        this.addError('L\'accantonamento TFR è obbligatorio', 'accantonamentoTfr');
      }

      if (!this.costo.altriCosti && this.costo.altriCosti !== 0) {
        this.addError('Gli altri costi sono obbligatori', 'altriCosti');
      }

      if (!this.costo.begin) {
        this.addError('Il mese di assunzione è obbligatorio', 'begin');
      }

      if (this.costo.end && this.costo.end < this.costo.begin) {
        this.addError('Il mese di cessazione deve essere successivo al primo', 'end');
      }

      return !this.hasErrors;
    },
    saveCosto () {
      if (!this.validate()) {
        return;
      }
      this.$emit('saved', this.costo);
    },
  },
};

</script>
